"use client";

import React from "react";
import { ArrowLeft, Image } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


declare const wvcClient: any;

function ProjectConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 max-w-5xl py-12 md:py-24 animate-pulse">
        <div className="h-10 w-40 bg-muted rounded mb-10"></div>
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
          <div className="h-12 w-3/4 md:w-1/2 bg-muted rounded"></div>
          <div className="h-8 w-24 bg-muted rounded"></div>
        </div>
        <div className="h-px w-full bg-border mb-10"></div>
        <div className="aspect-video w-full bg-muted rounded-xl mb-16"></div>
        <div className="space-y-4 max-w-3xl">
          <div className="h-8 w-48 bg-muted rounded mb-6"></div>
          <div className="h-4 w-full bg-muted rounded"></div>
          <div className="h-4 w-full bg-muted rounded"></div>
          <div className="h-4 w-5/6 bg-muted rounded"></div>
          <div className="h-4 w-4/6 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 max-w-5xl py-24 text-center flex flex-col items-center justify-center min-h-[50vh]">
        <h2 className="text-3xl font-bold text-foreground mb-4">Project Not Found</h2>
        <p className="text-lg text-muted-foreground mb-8 max-w-md">
          The project you are looking for does not exist or could not be loaded at this time.
        </p>
        <Link to="/GalleryPage">
          <Button size="lg">
            <ArrowLeft className="mr-2 size-5" />
            Back to Gallery
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  
  let images: string[] = [];
  if (post.customFields?.project_images) {
    if (Array.isArray(post.customFields.project_images)) {
      images = post.customFields.project_images;
    } else if (typeof post.customFields.project_images === "string") {
      images = post.customFields.project_images
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  if (images.length === 0 && post.featuredImage) {
    images = [post.featuredImage];
  }

  return (
    <div className="container mx-auto px-4 max-w-5xl py-12 md:py-24">
      {}
      <div className="mb-8 md:mb-12">
        <Link to="/GalleryPage">
          <Button variant="ghost" className="pl-0 hover:bg-transparent hover:text-primary text-muted-foreground transition-colors">
            <ArrowLeft className="mr-2 size-4" />
            Back to Projects
          </Button>
        </Link>
      </div>

      {}
      <header className="mb-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-tight leading-tight">
            {post.title}
          </h1>
          {post.customFields?.project_category && (
            <Badge variant="secondary" className="text-sm md:text-base px-4 py-1.5 w-fit h-fit shadow-sm">
              {post.customFields.project_category}
            </Badge>
          )}
        </div>
        <Separator className="bg-border/60" />
      </header>

      {}
      {images.length > 0 ? (
        <div className="mb-16 relative group">
          <Carousel className="w-full rounded-2xl overflow-hidden shadow-xl border border-border/50 bg-card">
            <CarouselContent>
              {images.map((img, i) => (
                <CarouselItem key={i} data-index={i}>
                  <div className="aspect-[16/9] md:aspect-[21/9] relative bg-muted flex items-center justify-center overflow-hidden">
                    <img
                      src={img}
                      alt={`${post.title} - View ${i + 1}`}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {images.length > 1 && (
              <>
                <CarouselPrevious className="left-4 md:left-6 size-10 md:size-12 opacity-0 group-hover:opacity-100 transition-all duration-300 bg-background/90 backdrop-blur-md hover:bg-background hover:scale-110 border-none shadow-lg" />
                <CarouselNext className="right-4 md:right-6 size-10 md:size-12 opacity-0 group-hover:opacity-100 transition-all duration-300 bg-background/90 backdrop-blur-md hover:bg-background hover:scale-110 border-none shadow-lg" />
              </>
            )}
          </Carousel>
          {images.length > 1 && (
            <div className="text-center mt-4 text-sm text-muted-foreground font-medium">
              Swipe or use arrows to view more images
            </div>
          )}
        </div>
      ) : (
        <div className="mb-16 aspect-[16/9] md:aspect-[21/9] bg-muted/50 rounded-2xl flex flex-col items-center justify-center text-muted-foreground border border-dashed border-border">
          <Image className="size-16 mb-4 opacity-20" />
          <p className="text-lg font-medium">No images available for this project.</p>
        </div>
      )}

      {}
      <div className="max-w-3xl bg-card p-8 md:p-10 rounded-2xl shadow-sm border border-border/50">
        <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-6 font-serif">
          Project Overview
        </h2>
        <div className="text-base md:text-lg text-muted-foreground whitespace-pre-wrap leading-relaxed">
          {post.customFields?.project_description || post.content || "No detailed description has been provided for this project yet."}
        </div>
      </div>
    </div>
  );
}

export default function SingleCompletedProjectTemplateSection() {
  
  const currentWPQueryParams = typeof window !== "undefined" && window.wvcClient 
    ? window.wvcClient.getCurrentWPQueryParams() 
    : { post_type: "completed_project" };
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-completed-project-template-section" className="bg-background min-h-screen">
      <PostsProvider wp_query={wp_query}>
        <ProjectConsumer />
      </PostsProvider>
    </section>
  );
}